import type { ComponentPropsWithRef, ReactNode } from "react";

import { cn } from "../lib/cn";

export interface TableProps extends ComponentPropsWithRef<"table"> {
  /** Résumé lu par les lecteurs d'écran avant le tableau. */
  caption?: ReactNode;
}

/**
 * Tableau de données.
 *
 * Le conteneur en `overflow-x-auto` porte `tabindex=0` : sans lui, un tableau
 * large ne serait pas défilable au clavier seul (critère WCAG 2.1.1).
 */
export function Table({ className, caption, children, ...props }: TableProps) {
  return (
    <div
      tabIndex={0}
      role="region"
      aria-label={typeof caption === "string" ? caption : "Tableau de données"}
      className="overflow-x-auto rounded-xl border border-border focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
    >
      <table className={cn("w-full border-collapse text-left text-sm", className)} {...props}>
        {caption && <caption className="sr-only">{caption}</caption>}
        {children}
      </table>
    </div>
  );
}

export function TableHead({ className, ...props }: ComponentPropsWithRef<"thead">) {
  return (
    <thead
      className={cn("bg-surface-sunken text-xs tracking-wide text-fg-muted uppercase", className)}
      {...props}
    />
  );
}

export function TableBody({ className, ...props }: ComponentPropsWithRef<"tbody">) {
  return <tbody className={cn("divide-y divide-border", className)} {...props} />;
}

export interface TableRowProps extends ComponentPropsWithRef<"tr"> {
  selected?: boolean;
}

export function TableRow({ className, selected, ...props }: TableRowProps) {
  return (
    <tr
      aria-selected={selected}
      className={cn(
        "transition-colors",
        selected ? "bg-accent-subtle" : "hover:bg-surface-sunken",
        className,
      )}
      {...props}
    />
  );
}

/** `align` remplace l'attribut HTML obsolète du même nom, d'où le Omit. */
export interface TableHeaderCellProps extends Omit<ComponentPropsWithRef<"th">, "align"> {
  align?: "start" | "end";
}

export function TableHeaderCell({ className, align = "start", ...props }: TableHeaderCellProps) {
  return (
    <th
      // `scope` relie explicitement la colonne à ses cellules : c'est ce qui
      // permet d'annoncer « Statut : actif » et non juste « actif ».
      scope="col"
      className={cn(
        "px-4 py-3 font-medium whitespace-nowrap",
        align === "end" && "text-right",
        className,
      )}
      {...props}
    />
  );
}

export interface TableCellProps extends Omit<ComponentPropsWithRef<"td">, "align"> {
  align?: "start" | "end";
}

export function TableCell({ className, align = "start", ...props }: TableCellProps) {
  return (
    <td className={cn("px-4 py-3 text-fg", align === "end" && "text-right", className)} {...props} />
  );
}
